import { useState } from 'react';
import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import ToggleButton from '@mui/material/ToggleButton';
import { alpha, useTheme } from '@mui/material/styles';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';
import { PLAN_MORE_FEATURES, POWER_LISTING_TIERS } from 'src/utils/constants';

import { createCheckoutSession } from 'src/redux/slices/subscription';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const TIPS = {
  active:
    "A listing that's for sale on at least one marketplace. A listing on several marketplaces counts as one.",
  ai: 'One AI credit writes one full listing.',
  accounts: 'Accounts on the same marketplace, for example two eBay accounts or two Shopify stores.',
};

const ENTERPRISE_EMAIL = 'mailto:hello@voolist.com?subject=[Enterprise] Custom Power plan inquiry';

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString('en-US') : value);

// A label with an optional info tip, for the rows and the listings slot
function Label ({ children, tip }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary', typography: 'body2' }}>
      <span>{children}</span>
      {tip && (
        <Tooltip title={tip} arrow placement="top">
          <Box component="span" tabIndex={0} sx={{ display: 'inline-flex', cursor: 'help' }}>
            <Iconify icon="solar:info-circle-linear" width={14} sx={{ color: 'text.disabled' }} />
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
}

Label.propTypes = {
  children: PropTypes.node,
  tip: PropTypes.string,
};

// One limit: what it is on the left, the plan's amount on the right
function LimitRow ({ label, value, tip }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Label tip={tip}>{label}</Label>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Stack>
  );
}

LimitRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  tip: PropTypes.string,
};

// ----------------------------------------------------------------------

// A plan card, the same design as the website's pricing page. Every feature
// is on every plan, so a card only shows what differs: the price, the button,
// then the plan's limits as aligned rows, easy to compare across the three.
// The top half is the same height on every card (Power's listings picker sits
// in the slot where the others show their listings), so the buttons line up.
// Starter lists the headline features, Pro and Power what they add, and
// "Every other feature" opens the rest (PLAN_MORE_FEATURES) on all three
// cards at once (`showAll`), so they stay level. Pro is the lifted card.
// The button starts checkout for the chosen plan and billing period.
export default function PricingCard ({ card, yearly, titleComponent = 'h3', showAll = false, onToggleAll, sx, ...other }) {
  const theme = useTheme();
  const { subscription, price, caption, limits, annual, lists = [] } = card;
  const pro = subscription === 'pro';
  const power = subscription === 'power';

  const [selectedTierIndex, setSelectedTierIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedTier = power ? POWER_LISTING_TIERS[selectedTierIndex] : null;
  const isEnterprise = selectedTier?.contactSales === true;

  const handleGetStarted = async () => {
    if (isEnterprise) {
      window.location.href = ENTERPRISE_EMAIL;
      return;
    }

    const billingType = yearly ? 'annual' : 'monthly';
    // Power sends the chosen listings tier, the other plans their own name
    const planId = selectedTier ? selectedTier.id : subscription;

    trackEvent(ANALYTICS_EVENTS.BILLING_PLAN_SELECTED, { plan: planId, billingCycle: billingType });

    try {
      setLoading(true);
      setError(null);
      const { url } = await createCheckoutSession(planId, billingType);
      window.location.href = url;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError("The payment page didn't open. Try again.");
      setLoading(false);
    }
  };

  // The plan's limits, from the chosen Power tier when there is one
  const plan = selectedTier && !isEnterprise ? selectedTier : limits;
  const currentPrice = yearly ? (plan.annual ?? annual) : (plan.price ?? price);

  const renderPrice = isEnterprise ? (
    <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, lineHeight: '56px', letterSpacing: '-0.02em' }}>
      Talk to Us
    </Typography>
  ) : (
    <AnimatePresence mode="wait" initial={false}>
      <m.div
        key={currentPrice}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.15 }}
      >
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography component="span" sx={{ fontSize: '1.5rem', fontWeight: 700, alignSelf: 'flex-start', mt: 0.75 }}>
            $
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: '3rem', fontWeight: 800, lineHeight: '56px', letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}
          >
            {currentPrice}
          </Typography>
          <Typography component="span" sx={{ color: 'text.secondary', typography: 'body2', ml: 0.5 }}>
            / month
          </Typography>
        </Stack>
      </m.div>
    </AnimatePresence>
  );

  // The main reason to pick a plan, in the same slot on every card
  const renderListingsSlot = (
    <Box>
      <Label tip={TIPS.active}>Active listings</Label>
      {power ? (
        <ToggleButtonGroup
          value={selectedTierIndex}
          exclusive
          aria-label="Active listings"
          onChange={(_, newIndex) => {
            if (newIndex !== null) setSelectedTierIndex(newIndex);
          }}
          size="small"
          sx={{
            mt: 1,
            width: 1,
            border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
            '& .MuiToggleButton-root': {
              flex: 1,
              height: 32,
              border: 0,
              fontSize: '0.875rem',
              fontWeight: 700,
              textTransform: 'none',
            },
            '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
              color: 'primary.dark',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
            },
          }}
        >
          {POWER_LISTING_TIERS.map((tier, index) => (
            <ToggleButton key={tier.id} value={index}>
              {tier.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : (
        // The same frame as Power's picker, with the plan's one amount chosen
        <Box sx={{ mt: 1, p: 0.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}` }}>
          <Box
            sx={{
              height: 32,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: 'primary.dark',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
            }}
          >
            {formatCount(limits.activeListings)}
          </Box>
        </Box>
      )}
    </Box>
  );

  // The card's short list: a line ending in ":" is its heading
  const heading = lists[0]?.endsWith(':') ? lists[0] : null;
  const items = heading ? lists.slice(1) : lists;

  const renderItems = (list) => (
    <Stack component="ul" spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {list.map((item) => (
        <Stack key={item} component="li" direction="row" alignItems="flex-start" spacing={1.25}>
          <Iconify icon="eva:checkmark-fill" width={18} sx={{ mt: '1px', flexShrink: 0, color: 'primary.main' }} />
          <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.45 }}>{item}</Typography>
        </Stack>
      ))}
    </Stack>
  );

  const sectionSx = { mt: 2.5, pt: 2.5, borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.16)}` };

  const renderFeatures = lists.length > 0 && (
    <Box sx={sectionSx}>
      {heading && <Typography sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 700 }}>{heading}</Typography>}
      {renderItems(items)}
    </Box>
  );

  // Every other feature, opened from the button below
  const moreId = `${subscription}-more-features`;
  const renderMore = showAll && (
    <Box
      id={moreId}
      component={m.div}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      sx={sectionSx}
    >
      <Typography sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 700 }}>Also on every plan:</Typography>
      {renderItems(PLAN_MORE_FEATURES)}
    </Box>
  );

  const renderLimits = (
    <Stack spacing={1.5}>
      <LimitRow label="Drafts and inactive" value={isEnterprise ? 'Unlimited' : formatCount(plan.inactiveListings)} />
      <LimitRow label="AI listings a month" tip={TIPS.ai} value={isEnterprise ? 'Unlimited' : formatCount(plan.aiCredits)} />
      <LimitRow
        label="Accounts per marketplace"
        tip={TIPS.accounts}
        value={isEnterprise ? 'Unlimited' : formatCount(plan.accountsPerMarketplace)}
      />
    </Stack>
  );

  const buttonLabel = isEnterprise ? 'Contact Sales' : 'Get Started Now';

  return (
    <Box
      sx={{
        position: 'relative',
        height: 1,
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 3, md: 3.5 },
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
        boxShadow: `0 1px 2px ${alpha(theme.palette.grey[900], 0.04)}, 0 16px 32px -20px ${alpha(theme.palette.grey[900], 0.18)}`,
        // On phones the recommended plan comes first
        order: { xs: pro ? -1 : 0, md: 0 },
        // Pro: a 2px green edge drawn as a ring, so its contents sit exactly
        // where the other cards' do
        ...(pro && {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 28px 56px -24px ${alpha(theme.palette.primary.darker, 0.35)}`,
        }),
        ...sx,
      }}
      {...other}
    >
      {pro && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            whiteSpace: 'nowrap',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            color: 'common.white',
            bgcolor: 'primary.main',
          }}
        >
          Most popular
        </Box>
      )}

      <Typography component={titleComponent} sx={{ fontSize: '1.375rem', fontWeight: 700 }}>
        {subscription.charAt(0).toUpperCase() + subscription.slice(1)}
      </Typography>
      {/* Two lines tall on every card, so the prices line up */}
      <Typography sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.5, minHeight: '3em' }}>
        {caption}
      </Typography>

      <Box sx={{ mt: 2 }}>{renderPrice}</Box>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', height: 20 }}>
        {!isEnterprise && (yearly ? 'Billed yearly, 2 months free' : 'Billed monthly')}
      </Typography>

      <Box sx={{ mt: 2.5 }}>{renderListingsSlot}</Box>

      <LoadingButton
        onClick={handleGetStarted}
        fullWidth
        size="large"
        loading={loading}
        variant={isEnterprise ? 'outlined' : 'contained'}
        color={pro || isEnterprise ? 'primary' : 'inherit'}
        sx={{
          mt: 2.5,
          height: 48,
          fontSize: '1rem',
          fontWeight: 700,
          ...(!isEnterprise && { color: 'common.white' }),
        }}
      >
        {buttonLabel}
      </LoadingButton>

      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 1.25, minHeight: 18 }}>
        {error && (
          <Typography role="alert" sx={{ color: 'error.main', fontSize: '0.75rem' }}>
            {error}
          </Typography>
        )}
        {!error && !isEnterprise && (
          <>
            <Iconify icon="solar:shield-check-bold" width={14} sx={{ color: 'success.main' }} />
            <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>14-day money-back guarantee</Typography>
          </>
        )}
      </Stack>

      <Box sx={{ my: 2.5, borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.16)}` }} />

      {renderLimits}

      {renderFeatures}

      {renderMore}

      {/* Pinned to the bottom, so it lines up across the cards */}
      <Box sx={{ mt: 'auto', pt: 2.5 }}>
        <ButtonBase
          onClick={onToggleAll}
          aria-expanded={showAll}
          aria-controls={moreId}
          sx={{
            gap: 0.75,
            borderRadius: 0.75,
            // A bare button doesn't take the page's font on its own
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
            '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
          }}
        >
          <Iconify icon={showAll ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'} width={18} />
          {showAll ? 'Show fewer features' : 'Every other feature'}
        </ButtonBase>
      </Box>
    </Box>
  );
}

PricingCard.propTypes = {
  card: PropTypes.object,
  sx: PropTypes.object,
  yearly: PropTypes.number,
  // The plan name's heading level
  titleComponent: PropTypes.string,
  // Every other feature open (PricingView shares it across the cards)
  showAll: PropTypes.bool,
  onToggleAll: PropTypes.func,
};
